import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {SecurityTransaction, SecurityTransactionWithAccount} from "@/integrations/supabase/transaction.models.ts";
import logger from "@logger";

/**
 * Securities Transaction service managing security transactions
 */
class SecuritiesTransactionService extends BaseService {

    /**
     * Creates a new security transaction
     * @param {SecurityTransactionCreateRequest} request
     */
    async createSecurityTransaction(request: SecurityTransactionCreateRequest): Promise<SecurityTransaction> {
        logger.info("Creating security transaction", request);
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('securities_transactions')
            .insert(payload)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetches security transactions for a given security
     * @param {SecurityTransactionFetchBySecurityRequest} request
     */
    async fetchSecurityTransactionsForSecurity(request: SecurityTransactionFetchBySecurityRequest): Promise<SecurityTransaction[]> {
        const {securityId, userId, securityType } = request;
        const { data, error } = await supabase
            .from('securities_transactions')
            .select('*')
            .eq('security_type', securityType)
            .eq('security_id', securityId)
            .eq('user_id', userId)
            .order('transaction_date', { ascending: false });

        if (error) {
            await this.handleError(error);
            throw error;
        }
        return data;
    }

    /**
     * Fetch all security transactions for a given user
     * @param {SecurityTransactionFetchAllRequest} request request to fetch all security transactions
     */
    async fetchAllSecurityTransactions(request: SecurityTransactionFetchAllRequest): Promise<SecurityTransaction[]> {
        const { userId, securityType } = request;

        let query = supabase
            .from('securities_transactions')
            .select('*')
            .eq("user_id", userId)
            .order('transaction_date', { ascending: false });

        if (securityType) {
            query = query.eq('security_type', securityType)
        }

        const {data, error} = await query;

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetches a security transaction by its ID
     * @param {SecurityTransactionByIdRequest} request request to fetch a security transaction by its ID
     */
    async fetchSecurityTransaction(request: SecurityTransactionByIdRequest): Promise<SecurityTransactionWithAccount> {
        const { id, userId } = request;

        const { data, error } = await supabase
            .from('securities_transactions')
            .select('*, accounts(id, name, nickname, balance, currency, type, account_number)')
            .eq('id', id)
            .eq('user_id', userId)
            .single();

        if (error) {
            await this.handleError(error);
            if (error.code === 'PGRST116') {
                throw new Error('Security Transaction not found');
            }
            throw error;
        }

        return data;
    }
}

export default new SecuritiesTransactionService();
